import * as React from 'react';
import classNames from 'classnames/bind';

import { builder } from '../lib/validation';

import '../variables.css';
import css from './input.module.css';

const cx = classNames.bind(css);

export type OnValidation = (
  name: string,
  results: builder.BuilderValidationResult
) => void;

type Props = {
  disabled?: boolean;
  invalid?: boolean;
  fullWidth?: boolean;
  validators?: builder.UserValidators;
  invalidMessage?: string;
  multipleErrors?: boolean;
  onValidation?: OnValidation;
  validateOnChange?: boolean;
} & React.HTMLProps<HTMLInputElement>;

/**
 * Renders an input element. You can pass props that are accepted by the vanilla
 * `<input>` element besides the props accepted by the component itself.
 */
export const Input: React.FC<Props> = props => {
  const {
    className,
    disabled = false,
    fullWidth = false,
    invalid = false,
    invalidMessage,
    validators,
    multipleErrors = false,
    onChange,
    onFocus,
    onBlur,
    onValidation,
    placeholder,
    name,
    autoFocus,
    validateOnChange = true,
    ...restProps
  } = props;
  const inputRef = React.useRef(null);
  const [validState, setValidState] = React.useState(!invalid);
  const [validationResults, setValidationResults] = React.useState<
    builder.BuilderValidationResult['results']
  >([]);
  const [focused, setFocused] = React.useState(!!autoFocus);
  const validate = React.useMemo(
    () => (validators ? builder.createBuilder(validators) : null),
    [validators]
  );

  const validateInputValue = React.useCallback(
    (value: string) => {
      if (validate) {
        const { valid, results } = validate(value);
        setValidationResults(results);
        setValidState(valid);
      }
    },
    [validate]
  );

  const handleChange = React.useCallback(
    event => {
      if (validateOnChange) {
        validateInputValue(event.target.value);
      }

      onChange?.(event);
    },
    [validateInputValue, onChange, validateOnChange]
  );

  const handleFocus = React.useCallback(
    event => {
      setFocused(true);
      onFocus?.(event);
    },
    [onFocus]
  );

  React.useEffect(() => {
    onValidation?.(name, { valid: validState, results: validationResults });
  }, [name, onValidation, validationResults, validState]);

  const handleBlur = React.useCallback(
    event => {
      validateInputValue(event.target.value);

      if (invalid) {
        setValidState(false);
      }

      setFocused(false);
      onBlur?.(event);
    },
    [onBlur, invalid, validateInputValue]
  );

  let errorContent: React.ReactNode = null;

  if (!focused && !validState) {
    if (invalidMessage) {
      errorContent = <li>{invalidMessage}</li>;
    } else if (validationResults.length) {
      if (multipleErrors) {
        errorContent = validationResults.map(result => (
          <li key={result.type}>{result.message}</li>
        ));
      } else {
        errorContent = <li>{validationResults[0].message}</li>;
      }
    }
  }

  return (
    <div className={cx('wrapper', className)}>
      <div>
        <input
          ref={inputRef}
          className={cx('input', {
            fullwidth: fullWidth,
            disabled,
            invalid: focused ? false : !validState,
          })}
          name={name}
          aria-label={placeholder}
          placeholder={placeholder}
          disabled={disabled}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          autoFocus={autoFocus}
          {...restProps}
        />
      </div>
      <ul className={cx('error')}>{errorContent}</ul>
    </div>
  );
};

Input.defaultProps = {
  disabled: false,
  invalid: false,
};
